// RollingRangeWidget/RollingRangeEngine.js
// Engine Pattern (rk=f03ae8b6) — extends EngineCleanupBase, singleton export.
// bootup+fetch() called from PageContent/BootRegistry at app boot (same as
// Sma200Engine). RMQ push handler ready for whenever a BE event exists (see
// Sma200's SignalR-wiring Issue, rk=feb49f53a53946e99a16835e7ab545c4 — same situation).
import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import rollingRangeCache from './RollingRangeCache';
import rollingRangeFetcher from './RollingRangeFetcher';

const _name = 'RollingRangeEngine';

class RollingRangeEngine extends EngineCleanupBase {
    bootup = () => {
        this.initialize(_name);
    };

    /**
     * Initial fetch for ALL assets, ALL windows (sid=0, window=0) — one call, called
     * once from PageContent alongside the other engines' fetch/fetchAll calls.
     * @returns {Promise<void>}
     */
    fetch = async () => {
        try {
            const items = await rollingRangeFetcher.fetchAll();
            if (!items.length) return;

            rollingRangeCache.addOrUpdate(items);
            this._notify(null);
        } catch (error) {
            this.logError('fetch', error.message);
        }
    };

    /**
     * RMQ push handler. CacheBase2.addOrUpdate() requires an array (it calls
     * data.forEach) — wraps a bare single object into one.
     * @param {object|object[]} data
     */
    onRollingRangeReceived = (data) => {
        try {
            const items = Array.isArray(data) ? data : [data];
            rollingRangeCache.addOrUpdate(items);
            this._notify(items[0]?.sid ?? null);
        } catch (error) {
            this.logError('onRollingRangeReceived', error.message);
        }
    };

    /**
     * One window's candle for an asset. rk is deterministic ("{sid}_{window}"), so a
     * direct cache.get() is enough — no need to filter/scan getAll().
     * @param {number} sid
     * @param {7|14|30|90} window
     * @returns {{ov:number,hv:number,lv:number,cv:number,oh:number,ol:number,oc:number,lh:number,ems:number}|null}
     */
    getState = (sid, window) => rollingRangeCache.get(`${sid}_${window}`);

    /**
     * All 4 window-candles for an asset (7/14/30/90), whichever are cached.
     * @param {number} sid
     * @returns {object[]}
     */
    getAllWindows = (sid) => rollingRangeCache.getAll().filter((i) => i.sid === sid);

    /**
     * @param {number|null} sid - which asset changed, or null for a bulk refresh
     */
    _notify = (sid) => {
        ComponentNotifier.notify(_name, 'dataChanged', { sid });
    };
}

const rollingRangeEngine = new RollingRangeEngine();
export default rollingRangeEngine;
